import React, { useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import { fabric } from 'fabric';
import { issueReportsAPI } from '../../services/api';
import diagnosticsCollector from '../../services/diagnosticsCollector';
import './ReportIssueModal.css';

const ReportIssueModal = ({ context = {}, screenshot, onClose, reduxStore }) => {
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('General');
    const [assignTo, setAssignTo] = useState('-- Select Assignment --');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [includeServerDiagnostics, setIncludeServerDiagnostics] = useState(true);
    const [diagnosticsSize, setDiagnosticsSize] = useState(0);
    const modalRef = useRef(null);
    const canvasRef = useRef(null);
    const fabricCanvas = useRef(null);
    const [currentTool, setCurrentTool] = useState('rectangle');
    const [currentColor, setCurrentColor] = useState('#FF0000');



    useEffect(() => {
        if (screenshot && canvasRef.current) {
            fabricCanvas.current = new fabric.Canvas(canvasRef.current);
            fabric.Image.fromURL(screenshot, (img) => {
                const canvasWidth = canvasRef.current.parentElement.clientWidth;
                const scale = canvasWidth / img.width;
                const canvasHeight = img.height * scale;
                fabricCanvas.current.setWidth(canvasWidth);
                fabricCanvas.current.setHeight(canvasHeight);
                img.set({
                    scaleX: scale,
                    scaleY: scale,
                    selectable: false,
                    evented: false,
                });
                fabricCanvas.current.setBackgroundImage(img, fabricCanvas.current.renderAll.bind(fabricCanvas.current));
            });
        }

        return () => {
            if (fabricCanvas.current) {
                fabricCanvas.current.dispose();
            }
        };
    }, [screenshot]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!description.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Description Required',
                text: 'Please provide a detailed description of the issue.'
            });
            return;
        }

        if (assignTo === '-- Select Assignment --') {
            Swal.fire({
                icon: 'warning',
                title: 'Assignment Required',
                text: 'Please select who this issue should be assigned to.'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const annotatedScreenshot = includeScreenshot && fabricCanvas.current ? fabricCanvas.current.toDataURL('image/png') : '';

            // Collect comprehensive diagnostic data
            const diagnosticData = diagnosticsCollector.collectAll({
                store: reduxStore,
                pageState: {
                    selectedActivity: context.selectedActivity,
                    selectedTahun: context.selectedTahun,
                    activityId: context.activityId,
                    activityName: context.activityName,
                    enrollmentYear: context.enrollmentYear
                },
                tableData: context.tableData
            });

            // Calculate diagnostic data size
            const dataSize = diagnosticsCollector.getDataSize(diagnosticData);
            const dataSizeMB = (dataSize / 1024 / 1024).toFixed(2);

            console.log(`ReportIssue: Diagnostic data size: ${dataSizeMB} MB`);

            // Warn if data is too large
            if (dataSize > 5 * 1024 * 1024) { // 5MB
                const confirmed = await Swal.fire({
                    icon: 'warning',
                    title: 'Large Diagnostic Data',
                    text: `The diagnostic data is ${dataSizeMB} MB. This may take longer to upload. Continue?`,
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Continue',
                    cancelButtonText: 'Cancel'
                });

                if (!confirmed.isConfirmed) {
                    setIsSubmitting(false);
                    return;
                }
            }

            const issueData = {
                description: description,
                screen: context.screen || 'Student Uniform',
                category: category,
                assignTo: assignTo,
                url: window.location.href,
                userAgent: navigator.userAgent,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                activityId: context.activityId ? context.activityId.toString() : null,
                activityName: context.activityName || null,
                selectedTahun: context.selectedTahun ? context.selectedTahun.toString() : null,
                enrollmentYear: context.enrollmentYear ? context.enrollmentYear.toString() : new Date().getFullYear().toString(),
                consoleLogs: JSON.stringify(diagnosticData.consoleLogs),
                tableData: context.tableData ? JSON.stringify(
                    Array.isArray(context.tableData)
                        ? context.tableData.slice(0, 50)  // Limit to first 50 rows
                        : context.tableData
                ) : null,
                pageState: JSON.stringify(diagnosticData.pageState),
                screenshotBase64: annotatedScreenshot || null,
                // Enhanced diagnostic data
                browserInfo: JSON.stringify(diagnosticData.browserInfo),
                performanceMetrics: JSON.stringify(diagnosticData.performance),
                networkInfo: JSON.stringify(diagnosticData.network),
                apiCallHistory: JSON.stringify(diagnosticData.apiCallHistory),
                jsErrors: JSON.stringify(diagnosticData.jsErrors),
                clientDiagnostics: JSON.stringify({
                    localStorage: diagnosticData.localStorage,
                    sessionStorage: diagnosticData.sessionStorage,
                    reduxState: diagnosticData.reduxState,
                    capabilities: diagnosticData.capabilities,
                    viewportSize: diagnosticData.viewportSize
                }),
                includeServerDiagnostics: includeServerDiagnostics
            };

            console.log('ReportIssue: Submitting issue data:', {
                ...issueData,
                screenshotBase64: annotatedScreenshot ? '[BASE64_DATA]' : null,
                browserInfo: '[DIAGNOSTIC_DATA]',
                performanceMetrics: '[DIAGNOSTIC_DATA]',
                clientDiagnostics: '[DIAGNOSTIC_DATA]'
            });

            const response = await issueReportsAPI.createIssueReport(issueData);

            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Issue Reported',
                    text: 'Your issue has been successfully reported. Thank you for helping us improve!',
                    confirmButtonText: 'OK'
                });
                onClose();
            } else {
                throw new Error(response.message || 'Failed to submit issue report');
            }
        } catch (error) {
            console.error('Error submitting issue report:', error);
            console.error('Error response data:', JSON.stringify(error.response?.data, null, 2));
            console.error('Error response status:', error.response?.status);
            console.error('Full error object:', JSON.stringify({
                message: error.message,
                response: error.response ? {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                } : null
            }, null, 2));

            const errorMessage = error.response?.data?.message || error.message || 'Failed to submit issue report. Please try again.';
            const errors = error.response?.data?.errors;
            let errorDetails = '';

            if (errors && Array.isArray(errors) && errors.length > 0) {
                errorDetails = '<ul style="text-align: left; font-size: 12px;">' +
                    errors.map(e => `<li>${e}</li>`).join('') +
                    '</ul>';
            }

            Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                html: `<p>${errorMessage}</p>${errorDetails}`
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToolClick = (tool) => {
        setCurrentTool(tool);
        const canvas = fabricCanvas.current;
        canvas.isDrawingMode = false;
        canvas.off('mouse:down');
        canvas.off('mouse:move');
        canvas.off('mouse:up');

        if (tool === 'rectangle') {
            enableRectangleDrawing(canvas);
        } else if (tool === 'arrow') {
            enableArrowDrawing(canvas);
        } else if (tool === 'text') {
            enableTextAdding(canvas);
        }
    };

    const enableRectangleDrawing = (canvas) => {
        let isDrawing = false;
        let rect;

        canvas.on('mouse:down', (o) => {
            isDrawing = true;
            const pointer = canvas.getPointer(o.e);
            rect = new fabric.Rect({
                left: pointer.x,
                top: pointer.y,
                width: 0,
                height: 0,
                fill: 'transparent',
                stroke: currentColor,
                strokeWidth: 2
            });
            canvas.add(rect);
        });

        canvas.on('mouse:move', (o) => {
            if (!isDrawing) return;
            const pointer = canvas.getPointer(o.e);
            rect.set({
                width: Math.abs(pointer.x - rect.left),
                height: Math.abs(pointer.y - rect.top)
            });
            canvas.renderAll();
        });

        canvas.on('mouse:up', () => {
            isDrawing = false;
        });
    };

    const enableArrowDrawing = (canvas) => {
        let isDrawing = false;
        let line, arrowHead;

        canvas.on('mouse:down', (o) => {
            isDrawing = true;
            const pointer = canvas.getPointer(o.e);
            const points = [pointer.x, pointer.y, pointer.x, pointer.y];
            line = new fabric.Line(points, {
                stroke: currentColor,
                strokeWidth: 2,
                selectable: false
            });
            canvas.add(line);
        });

        canvas.on('mouse:move', (o) => {
            if (!isDrawing) return;
            const pointer = canvas.getPointer(o.e);
            line.set({ x2: pointer.x, y2: pointer.y });
            canvas.renderAll();
        });

        canvas.on('mouse:up', (o) => {
            isDrawing = false;
            const pointer = canvas.getPointer(o.e);
            const angle = Math.atan2(pointer.y - line.y1, pointer.x - line.x1);
            const headLength = 10;

            arrowHead = new fabric.Triangle({
                left: pointer.x,
                top: pointer.y,
                originX: 'center',
                originY: 'center',
                selectable: false,
                angle: (angle * 180) / Math.PI + 90,
                width: 10,
                height: 10,
                fill: currentColor
            });
            canvas.add(arrowHead);
            canvas.renderAll();
        });
    };

    const enableTextAdding = (canvas) => {
        canvas.on('mouse:down', (o) => {
            const pointer = canvas.getPointer(o.e);
            const text = new fabric.IText('Type here', {
                left: pointer.x,
                top: pointer.y,
                fill: currentColor,
                fontSize: 20
            });
            canvas.add(text);
            canvas.setActiveObject(text);
            text.enterEditing();
            text.selectAll();
            canvas.off('mouse:down');
        });
    };

    const clearAnnotations = () => {
        const canvas = fabricCanvas.current;
        const objects = canvas.getObjects().filter(obj => obj !== canvas.backgroundImage);
        objects.forEach(obj => canvas.remove(obj));
        canvas.renderAll();
    };

    return (
        <div className="report-issue-modal-overlay">
            <div className="report-issue-modal-full" ref={modalRef}>
                <div className="modal-header">
                    <h2><i className="fas fa-bug"></i> Report Issue - Screen: {context.screen || 'Student Uniform'}</h2>
                    <button className="close-button" onClick={onClose} aria-label="Close">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body-full">
                    <div className="annotation-sidebar">
                        <div className="tools-section">
                            <button className={`tool-btn ${currentTool === 'rectangle' ? 'active' : ''}`} onClick={() => handleToolClick('rectangle')} title="Rectangle">
                                <i className="fas fa-square"></i>
                            </button>
                            <button className={`tool-btn ${currentTool === 'arrow' ? 'active' : ''}`} onClick={() => handleToolClick('arrow')} title="Arrow">
                                <i className="fas fa-long-arrow-alt-right"></i>
                            </button>
                            <button className={`tool-btn ${currentTool === 'text' ? 'active' : ''}`} onClick={() => handleToolClick('text')} title="Text">
                                <i className="fas fa-font"></i>
                            </button>
                        </div>
                        <div className="color-picker">
                            {['#FF0000', '#0000FF', '#00FF00', '#FFFF00', '#000000'].map(color => (
                                <button key={color} className={`color-btn ${currentColor === color ? 'active' : ''}`} style={{ backgroundColor: color }} onClick={() => setCurrentColor(color)}></button>
                            ))}
                        </div>
                        <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} className="form-control" required>
                                <option value="General">General</option>
                                <option value="Bug">Bug</option>
                                <option value="Performance">Performance</option>
                                <option value="UI">UI</option>
                                <option value="Usage">Usage</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">Description *</label>
                            <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} className="form-control" placeholder="Please provide a detailed description..." rows="5" required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="assignTo">Assign To *</label>
                            <select id="assignTo" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className="form-control" required>
                                <option value="-- Select Assignment --">-- Select Assignment --</option>
                                <option value="PPD">PPD</option>
                                <option value="Admin">Admin</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={includeScreenshot} onChange={() => setIncludeScreenshot(!includeScreenshot)} />
                                Include Screenshot
                            </label>
                        </div>
                        <div className="form-group">
                            <label>
                                <input type="checkbox" checked={includeServerDiagnostics} onChange={() => setIncludeServerDiagnostics(!includeServerDiagnostics)} />
                                Include Server Diagnostics
                            </label>
                            <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                                Captures server logs, performance, and database state
                            </small>
                        </div>
                        <button className="btn btn-secondary" onClick={clearAnnotations}>Clear All</button>
                        <div className="form-actions">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                            <button type="submit" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : 'Submit Report'}
                            </button>
                        </div>
                    </div>
                    <div className="canvas-container">
                        {screenshot ? (
                            <canvas ref={canvasRef} />
                        ) : (
                            <p>Loading screenshot...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportIssueModal;
